import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import uuid from 'uuid/v4';
import {fromJS, List, Map} from 'immutable';
import TextInput from 'generic/Forms/TextInput/TextInput.react';
import SupplementManagerModalButton from 'client/Supplements/SupplementManager/SupplementManagerModalButton.react';
import Button from 'sg/Button/Button.react';
import {AuthoringQuestionModelV1} from 'resources/augmented/AuthoringQuestion/AuthoringQuestionModel.v1';
import {SortableContainer, SortableElement, SortableHandle} from 'react-sortable-hoc';
import AuthoringTextInput from 'client/EditPage/V2/AuthoringTextInput/AuthoringTextInput.react';
import './fill-in-the-blank-editor.scss';

const SortableDropdownArea = SortableContainer(
  ({
    question,
    dropdowns,
    validResponse,
    onModelChange,
    validationErrors,
    onSortEnd,
    useDrageHandle,
    helperClass
  }) => {
    return (
      <DropdownEditArea
        question={question}
        dropdowns={dropdowns}
        validResponse={validResponse}
        onModelChange={onModelChange}
        validationErrors={validationErrors}
        onSortEnd={onSortEnd}
        useDrageHandle={useDrageHandle}
        helperClass={helperClass}
      />
    );
  }
);

const SortableDropdown = SortableElement(
  ({index, question, dropdown, validResponse, onModelChange, validationErrors, outerIndex}) => {
    return (
      <Dropdown
        key={index}
        index={outerIndex}
        question={question}
        dropdown={dropdown}
        validResponse={validResponse}
        onModelChange={onModelChange}
        validationErrors={validationErrors}
      />
    );
  }
);

const SortHandle = SortableHandle(() => {
  return (
    <div className='fill-in-the-blank-editor__sort-handle-wrapper'>
      <span className='fa fa-ellipsis-v' />
      <span className='fa fa-ellipsis-v' />
    </div>
  );
});

export default class FillInTheBlankEditor extends React.Component {
  static propTypes = {
    question: PropTypes.instanceOf(AuthoringQuestionModelV1),
    onModelChange: PropTypes.func,
    validationErrors: PropTypes.instanceOf(Map),
    onBlur: PropTypes.func
  };

  static defaultProps = {
    onBlur: () => {}
  };

  constructor(props) {
    super(props);
    this.state = {
      cursor: props.question.getDropdownText().length
    };
  }

  onChange = (e) => {
    const updatedQuestion = this.props.question.setDropdownText(e.target.value);
    this.props.onModelChange(updatedQuestion, 'dropdownText');
  };

  handleBlur = (e) => {
    this.props.onBlur(e);
    this.setState({cursor: e.target.selectionStart});
  };

  insertDropdownString = (e) => {
    e.stopPropagation();
    const {cursor} = this.state;
    const {question} = this.props;

    const text = question.getDropdownText();
    const left = text.slice(0, cursor);
    const right = text.slice(cursor);
    const updatedText = `${left} {{dropdown}} ${right}`;

    const updatedQuestion = question.setDropdownText(updatedText);
    this.props.onModelChange(updatedQuestion, 'dropdownText');
  };

  addDropdown = () => {
    const dropdowns = this.props.question.getDropdowns();
    const newDropdown = fromJS({
      id: uuid(),
      position: dropdowns.size,
      choices: [
        {
          id: uuid(),
          value: ''
        }
      ]
    });

    const updatedDropdowns = dropdowns
      .push(newDropdown)
      .sortBy((dropdown) => dropdown.get('position'))
      .map((dropdown, position) => dropdown.set('position', position));
    const updatedQuestion = this.props.question.setDropdowns(updatedDropdowns);

    this.props.onModelChange(updatedQuestion, 'dropdowns');
  };

  onSortEnd = ({oldIndex, newIndex}) => {
    if (oldIndex === newIndex) {
      return;
    }

    const normalizeIndices = (dropdown, i) => dropdown.set('position', i);
    const dropdowns = this.props.question.getDropdowns().map(normalizeIndices);
    const dropdownToMove = dropdowns.get(oldIndex);
    const updatedDropdowns = dropdowns
      .delete(oldIndex)
      .insert(newIndex, dropdownToMove)
      .map(normalizeIndices);

    const updatedQuestion = this.props.question.setDropdowns(updatedDropdowns);

    this.props.onModelChange(updatedQuestion);
  };

  render() {
    const errors = this.props.validationErrors;
    const dropdownTextErrors = errors.get('dropdownText');
    const dropdownsError =
      errors.get('dropdowns') && typeof errors.get('dropdowns') === 'string' ? (
        <div className='a-form-input__error'>{errors.get('dropdowns')}</div>
      ) : null;
    return (
      <div className='fill-in-the-blank-editor'>
        <AuthoringTextInput
          value={this.props.question.getDropdownText()}
          error={dropdownTextErrors}
          onChange={this.onChange}
          className='question-editor__question-detail--fullwidth'
          label='Dropdown Prompt'
          name='dropdownText'
          onBlur={this.handleBlur}
          data-field-path='dropdown_text'
        />
        <div className='u-display_flex'>
          <Button
            className='fill-in-the-blank-editor__insert-dropdown-btn'
            color='gray'
            onClick={this.insertDropdownString}
          >
            Insert dropdown
          </Button>
          <SupplementManagerModalButton
            /**
             * The `SupplementManagerModalButton` winds up triggering
             * `INSERT_SUPPLEMENT_AT_CURSOR_POSITION`, the handler for
             * this method does a `setFieldIn` so we **must** use the
             * actual property name, not the setter name.
             */
            pathToProperty='dropdown_text'
          />
        </div>
        {dropdownsError}
        <SortableDropdownArea
          question={this.props.question}
          dropdowns={this.props.question.getDropdowns()}
          validResponse={this.props.question.getValidResponse()}
          onModelChange={this.props.onModelChange}
          validationErrors={this.props.validationErrors}
          onSortEnd={this.onSortEnd}
          useDrageHandle
          helperClass='fill-in-the-blank-editor__drag-helper'
        />
        <Button onClick={this.addDropdown} color='green' text='New dropdown' />
      </div>
    );
  }
}

class DropdownEditArea extends React.Component {
  static propTypes = {
    question: PropTypes.instanceOf(AuthoringQuestionModelV1),
    dropdowns: PropTypes.instanceOf(List),
    validResponse: PropTypes.instanceOf(Map),
    onModelChange: PropTypes.func,
    validationErrors: PropTypes.instanceOf(Map)
  };

  generateDropdowns() {
    return this.props.dropdowns.map((dropdown, index) => {
      return (
        <SortableDropdown
          index={index}
          key={index}
          outerIndex={index}
          question={this.props.question}
          dropdown={dropdown}
          validResponse={this.props.validResponse}
          onModelChange={this.props.onModelChange}
          validationErrors={this.props.validationErrors}
        />
      );
    });
  }

  render() {
    return <div className='fill-in-the-blank-dropdown-editor'>{this.generateDropdowns()}</div>;
  }
}

class Dropdown extends React.Component {
  static propTypes = {
    question: PropTypes.instanceOf(AuthoringQuestionModelV1),
    dropdown: PropTypes.object,
    validResponse: PropTypes.instanceOf(Map),
    onModelChange: PropTypes.func,
    validationErrors: PropTypes.instanceOf(Map),
    index: PropTypes.number
  };

  deleteDropdown = () => {
    const dropdowns = this.props.question
      .getDropdowns()
      .filterNot((dropdown) => dropdown.get('id') === this.props.dropdown.getId())
      .map((dropdown, index) => dropdown.set('position', index));
    const validResponse = this.props.question
      .getValidResponse()
      .remove(this.props.dropdown.getId());

    const updatedQuestion = this.props.question
      .setDropdowns(dropdowns)
      .setValidResponse(validResponse);

    this.props.onModelChange(updatedQuestion, 'dropdowns');
  };

  addChoice = () => {
    const choices = this.props.question
      .getDropdowns()
      .getIn([this.props.dropdown.getPosition(), 'choices'])
      .push(
        fromJS({
          id: uuid(),
          value: ''
        })
      );
    const updatedDropdowns = this.props.question
      .getDropdowns()
      .setIn([this.props.dropdown.getPosition(), 'choices'], choices);
    const updatedQuestion = this.props.question.setDropdowns(updatedDropdowns);

    this.props.onModelChange(updatedQuestion, 'dropdowns');
  };

  generateChoices() {
    return this.props.dropdown.getChoices().map((choice, index) => {
      return (
        <DropdownChoice
          key={index}
          question={this.props.question}
          choice={choice}
          dropdown={this.props.dropdown}
          index={index}
          outerIndex={this.props.index}
          validResponse={this.props.validResponse}
          onModelChange={this.props.onModelChange}
        />
      );
    });
  }

  render() {
    const {dropdown} = this.props;
    const dropdownError = this.props.validationErrors.getIn([
      'rubric',
      this.props.dropdown.get('id')
    ]);

    return (
      <div className='fill-in-the-blank-editor__dropdown'>
        <h3 className='fill-in-the-blank-editor__dropdown-number'>
          Dropdown {dropdown.getPosition() + 1}
          <Button
            onClick={this.deleteDropdown}
            color='red'
            outline
            noBorder
            className='fa fa-trash'
          />
        </h3>
        {dropdownError ? <div className='a-form-input__error'>{dropdownError}</div> : null}
        <div className='fill-in-the-blank-editor__choices-wrapper'>
          <div className='fill-in-the-blank-editor__choices'>{this.generateChoices()}</div>
          <SortHandle />
        </div>
        <Button onClick={this.addChoice} text='Add choice' />
        <hr />
      </div>
    );
  }
}

class DropdownChoice extends React.Component {
  static propTypes = {
    question: PropTypes.instanceOf(AuthoringQuestionModelV1),
    dropdown: PropTypes.object,
    choice: PropTypes.object,
    index: PropTypes.number,
    validResponse: PropTypes.instanceOf(Map),
    onModelChange: PropTypes.func,
    outerIndex: PropTypes.number
  };

  onChange = (e) => {
    const updatedChoice = Map({
      id: this.props.choice.getId(),
      value: e.target.value
    });

    const updatedDropdowns = this.props.question
      .getDropdowns()
      .setIn([this.props.dropdown.getPosition(), 'choices', this.props.index], updatedChoice);

    const updatedQuestion = this.props.question.setDropdowns(updatedDropdowns);
    this.props.onModelChange(updatedQuestion);
  };

  selectChoice = () => {
    const validResponse = this.props.question
      .getValidResponse()
      .set(this.props.dropdown.getId(), this.props.choice.getId());

    const updatedQuestion = this.props.question.setValidResponse(validResponse);

    this.props.onModelChange(updatedQuestion, 'rubric');
  };

  deleteChoice = () => {
    const choiceId = this.props.question
      .getDropdowns()
      .getIn([this.props.dropdown.getPosition(), 'choices', this.props.index, 'id']);
    const updatedDropdowns = this.props.question
      .getDropdowns()
      .removeIn([this.props.dropdown.get('position'), 'choices', this.props.index]);
    let updatedQuestion = this.props.question.setDropdowns(updatedDropdowns);

    if (choiceId === updatedQuestion.getValidResponse().get(this.props.dropdown.getId())) {
      const validResponse = updatedQuestion.getValidResponse().remove(this.props.dropdown.getId());
      updatedQuestion = updatedQuestion.setValidResponse(validResponse);
    }

    this.props.onModelChange(updatedQuestion, 'rubric');
  };

  render() {
    const {dropdown, choice} = this.props;
    const fieldPath = `dropdowns,${this.props.outerIndex},choices,${this.props.index},value`;
    const dropdownId = dropdown.getId();
    const choiceId = choice.getId();
    const isCorrectChoice = this.props.validResponse.get(dropdownId) === choiceId;
    const inputClasses = classnames('fill-in-the-blank-editor__dropdown-choice-input', {
      'is-correct-choice': isCorrectChoice
    });

    return (
      <div>
        <div className='fill-in-the-blank-editor-dropdown-choice u-pad-t_2'>
          <input type='checkbox' checked={isCorrectChoice} onChange={this.selectChoice} />
          <TextInput
            className={inputClasses}
            name='value'
            onChange={this.onChange}
            placeholder='Type answer here.'
            value={choice.getValue()}
          />
          <Button
            className='fa fa-times'
            onClick={this.deleteChoice}
            color='red'
            outline
            noBorder
          />
        </div>
        <div className='fill-in-the-blank-editor__supplement-manager'>
          <SupplementManagerModalButton linkButton pathToProperty={fieldPath} color='blue' />
        </div>
      </div>
    );
  }
}
